import { useEffect, type ReactNode } from 'react';
import { useOrganizationThemeStore } from '../features/org/infrastructure/organization-theme.store';
import { useUserAppearanceStore } from '../features/auth/infrastructure/user-appearance.store';
import { applyColorScheme } from './apply-color-scheme';
import { applyOrganizationTheme } from './apply-organization-theme';
import { DEFAULT_ORGANIZATION_THEME } from './defaults';

interface ThemeProviderProps {
  children: ReactNode;
  /** When auth resolves, pass the signed-in user id so appearance prefs load per user. */
  userId?: string | null;
}

/**
 * Composes two independent theme layers:
 * - **Organization (account):** brand colours as CSS variables (`--color-primary`, `--color-brand-*`, …)
 * - **User:** light/dark color scheme via `data-color-scheme` + `.dark` class
 */
export function ThemeProvider({ children, userId = null }: ThemeProviderProps): JSX.Element {
  const initializeOrganizationTheme = useOrganizationThemeStore((state) => state.initialize);
  const hydrateForUser = useUserAppearanceStore((state) => state.hydrateForUser);

  useEffect(() => {
    applyOrganizationTheme(DEFAULT_ORGANIZATION_THEME.colors);
    const scheme = useUserAppearanceStore.getState().colorScheme;
    applyColorScheme(scheme);
    initializeOrganizationTheme();
  }, [initializeOrganizationTheme]);

  useEffect(() => {
    hydrateForUser(userId ?? null);
  }, [hydrateForUser, userId]);

  return <>{children}</>;
}

export { useTheme } from './use-theme';
